import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Lock, ArrowLeft } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useSite, absoluteUrl } from "../context/SiteContext";

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { settings } = useSite();
  const brand = settings.brand;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email, password);
      toast.success("Welcome back, admin.");
      navigate("/admin");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid credentials.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="min-h-screen bg-[#050A0F] flex items-center justify-center p-6 relative overflow-hidden">
      <div className="absolute inset-0 blueprint-grid opacity-40" />
      <div className="relative w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-[#94A3B8] hover:text-[#F39C12] mb-8">
          <ArrowLeft className="h-3 w-3" /> Back to site
        </Link>
        <div className="border border-[#1F3550] bg-[#0D1B2A] p-10">
          <div className="flex items-center gap-3 mb-8">
            <img src={absoluteUrl(brand.logoUrl)} alt="logo" className="h-12 w-12 object-contain" />
            <div>
              <div className="font-heading font-black tracking-wider text-[#F8FAFC]">{brand.name}</div>
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#F39C12]">Control Room</div>
            </div>
          </div>
          <Lock className="h-7 w-7 text-[#F39C12]" />
          <h1 className="mt-4 font-heading font-black text-2xl uppercase text-[#F8FAFC]">Admin Login</h1>
          <p className="mt-2 text-sm text-[#94A3B8]">Authorized personnel only.</p>

          <form onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="admin-login-form">
            <div>
              <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#94A3B8]">Email</label>
              <input
                type="email"
                data-testid="login-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-2 w-full bg-[#050A0F] border border-[#1F3550] text-[#F8FAFC] focus:border-[#F39C12] focus:ring-1 focus:ring-[#F39C12] px-4 py-3 outline-none"
              />
            </div>
            <div>
              <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#94A3B8]">Password</label>
              <input
                type="password"
                data-testid="login-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-2 w-full bg-[#050A0F] border border-[#1F3550] text-[#F8FAFC] focus:border-[#F39C12] focus:ring-1 focus:ring-[#F39C12] px-4 py-3 outline-none"
              />
            </div>
            <button
              type="submit"
              data-testid="login-submit"
              disabled={submitting}
              className="w-full bg-[#F39C12] text-[#050A0F] py-4 font-bold uppercase tracking-widest text-sm hover:bg-[#D68910] transition-colors disabled:opacity-50"
            >
              {submitting ? "Authenticating..." : "Sign In"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
